import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import SignaturePad from '../components/SignaturePad';
import api from '../api/axios';

const Profile = () => {
    const { user, updateUser } = useAuth();
    const { config } = useConfig();

    useEffect(() => {
        if (!user?.id) return;
        
        const fetchUserProfile = async () => {
            try {
                const res = await api.get(`/users/${user.id}`);
                const fetchedUser = res.data;
                // Check if relevant data has changed to avoid unnecessary re-renders/updates
                if (fetchedUser.sala_id !== user.sala_id || fetchedUser.sala_name !== user.sala_name) {
                    updateUser({ ...user, sala_id: fetchedUser.sala_id, sala_name: fetchedUser.sala_name });
                }
            } catch (error) {
                console.error("Error fetching real-time profile data", error);
            }
        };

        const interval = setInterval(fetchUserProfile, 5000);
        return () => clearInterval(interval);
    }, [user, updateUser]);

    const primaryColor = config?.primaryColor || '#1e40af';
    
    const textGradientStyle = {
        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent'
    };

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editData, setEditData] = useState({
        name: user?.name || '',
        last_name: user?.lastName || user?.last_name || '',
        signature: user?.signature || null
    });
    const [error, setError] = useState('');

    const handleSave = async () => {
        setIsSaving(true);
        setError('');
        try {
            const res = await api.put('/users/profile/update', editData);
            updateUser(res.data.user);
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating profile", err);
            setError('Error al actualizar el perfil');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="animate-fadeIn pb-32">
            <header className="mb-10 flex flex-col items-center text-center pt-8 relative">
                <div
                    className="w-24 h-24 rounded-[40px] flex items-center justify-center text-white text-4xl font-black mb-6 shadow-2xl relative"
                    style={{ backgroundColor: primaryColor }}
                >
                    {user?.name?.[0].toUpperCase()}
                    <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-full shadow-lg text-lg">✨</div>
                </div>
                {!isEditing ? (
                    <>
                        <h1 className="text-3xl font-black tracking-tight" style={textGradientStyle}>{user?.name} {user?.lastName || user?.last_name}</h1>
                        <p className="font-bold uppercase tracking-widest text-[10px] mt-2" style={textGradientStyle}>{user?.role} </p>
                    </>
                ) : (
                    <div className="flex flex-col gap-3 w-full max-w-xs mt-4">
                        <input
                            className="w-full bg-slate-100 border border-slate-200 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-bold shadow-inner"
                            style={textGradientStyle}
                            placeholder="Nombre"
                            value={editData.name}
                            onChange={e => setEditData({ ...editData, name: e.target.value })}
                        />
                        <input
                            className="w-full bg-slate-100 border border-slate-200 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 font-bold shadow-inner"
                            style={textGradientStyle}
                            placeholder="Apellidos"
                            value={editData.last_name}
                            onChange={e => setEditData({ ...editData, last_name: e.target.value })}
                        />
                    </div>
                )}
            </header>

            <div className="bg-white rounded-[40px] p-10 border border-slate-200 shadow-2xl space-y-10 relative overflow-hidden max-w-2xl mx-auto">
                <div className="absolute top-4 right-4 z-20">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="bg-white hover:bg-slate-50 border border-slate-200 p-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm"
                            style={textGradientStyle}
                        >
                            ✏️ Editar
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditData({ name: user?.name, last_name: user?.lastName || user?.last_name, signature: user?.signature });
                                    setError('');
                                }}
                                className="bg-gradient-to-r from-rose-500 to-red-600 hover:from-red-600 hover:to-rose-500 text-white p-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1"
                            >
                                ✕
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="bg-gradient-to-r from-emerald-400 to-emerald-600 hover:from-emerald-600 hover:to-emerald-400 text-white p-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 disabled:opacity-50 disabled:transform-none"
                            >
                                {isSaving ? '...' : '💾 GUARDAR'}
                            </button>
                        </div>
                    )}
                </div>
                {error && <p className="text-red-400 text-xs font-bold text-center absolute top-4 left-0 right-0">{error}</p>}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-purple-500 rounded-full blur-[100px] opacity-20"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 relative z-10">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest pl-1" style={textGradientStyle}>Correo Electrónico</label>
                        <p className="text-lg font-bold bg-slate-50 p-4 rounded-2xl border border-slate-100 shadow-inner" style={textGradientStyle}>{user?.email}</p>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest pl-1" style={textGradientStyle}>Identificación</label>
                        <p className="text-lg font-bold bg-slate-50 p-4 rounded-2xl border border-slate-100 shadow-inner" style={textGradientStyle}>{user?.identification || 'N/A'}</p>
                    </div>
                    {user?.sala_name && (
                        <div className="col-span-1 md:col-span-2 space-y-2 animate-fadeIn">
                            <label className="text-[10px] font-black uppercase tracking-widest pl-1" style={textGradientStyle}>Ubicación del Evento (Sala Registrada)</label>
                            <p className="text-lg font-bold bg-slate-50 p-4 rounded-2xl border border-slate-100 shadow-inner flex items-center gap-3" style={textGradientStyle}>
                                <span className="text-xl" style={{ WebkitTextFillColor: 'initial' }}>🏢</span> {user.sala_name}
                            </p>
                        </div>
                    )}
                </div>

                <div className="pt-10 border-t border-slate-200 flex flex-col items-center gap-6 relative z-10">
                    <p className="text-xs font-black uppercase tracking-widest text-center leading-relaxed" style={textGradientStyle}>Pase de Acceso <br />Virtual ID</p>
                    <div className="w-64 bg-white p-6 rounded-[35px] shadow-[0_0_50px_rgba(157,78,221,0.2)] flex flex-col items-center gap-4 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-tr from-purple-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
                        <div className="relative z-10 p-2 bg-white rounded-2xl">
                            {user?.id && (
                                <QRCodeSVG
                                    value={user.id.toString()}
                                    size={180}
                                    fgColor="#1c0042"
                                    level="H"
                                />
                            )}
                        </div>
                    </div>

                    {isEditing ? (
                        <div className="w-full max-w-md mt-6 flex flex-col items-center border-t border-slate-200 pt-6 relative z-10 space-y-4 px-4 sm:px-0">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest" style={textGradientStyle}>Modificar Firma</p>
                            <div className="w-full bg-slate-50 border border-slate-200 rounded-3xl p-2 shadow-inner">
                                <SignaturePad onSignatureChange={(sig) => setEditData({ ...editData, signature: sig })} />
                            </div>
                            {editData.signature && (
                                <img src={editData.signature} alt="Firma Nueva" className="max-h-16 object-contain mt-2" />
                            )}
                        </div>
                    ) : (
                        user?.signature && (
                            <div className="w-full max-w-md mt-6 flex flex-col items-center border-t border-slate-200 pt-6 relative z-10">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4" style={textGradientStyle}>Firma Registrada</p>
                                <img src={user.signature} alt="Firma del asistente" className="max-h-16 object-contain" />
                            </div>
                        )
                    )}
                    {!isEditing && <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2 relative z-10" style={textGradientStyle}>Muestra este código para ingresar</p>}
                </div>
            </div>
        </div>
    );
};

export default Profile;
